import SearchAPI from 'api/search';
import { Page } from 'utils/page-utils';
import { routeToSearch } from 'router/router-utils';
import CityAPI from 'api/city';
import { getRouteParam, getUrlParamAsObject, paramNameOptions, } from 'utils/url-utils';
import { fetchPlaceCoordinates } from './place-utils';
import { updateTimes } from 'store/search-request/search-request-actions';
import DestinationAPI from 'api/destination';
import ErrorUtils from 'utils/error-utils';
import { isMonthlyHourlySwitch } from './search/is-monthly-hourly-switch';
import { resetFilters } from 'store/filters/filters-actions';
export const getAirportCodeByDestinationId = async (id) => {
    try {
        const destination = await DestinationAPI.get(id.toString()).then(res => res.data.data);
        const { airport: { iata_code }, } = destination;
        return iata_code || '';
    }
    catch (error) {
        ErrorUtils.sendSentryException(error);
        return '';
    }
};
export const _getAddress = (selectedSuggestion) => {
    // The address is used in the Google Geocoding API.
    // Destination and Current Location we use in Search-Component are not valid addresses.
    if (selectedSuggestion.description === 'Destination' ||
        selectedSuggestion.description === 'Current Location' ||
        selectedSuggestion.id // event
    ) {
        return undefined;
    }
    return selectedSuggestion.description;
};
export const handleSearchUpdates = async ({ destination, event, dateTimeValues, selectedSuggestion, parkingSubType, originalCitySlug, originalLatitude, originalLongitude, originalPageType, originalParkingSubType, originalId, originalSearchString, timezone, dispatch, history, googleMapsLibraries, onClose, }) => {
    const { starts, ends, powerBookingPeriods, powerBookingSource, } = dateTimeValues;
    const address = _getAddress(selectedSuggestion);
    let suggestionLatLng = {};
    try {
        suggestionLatLng = await fetchPlaceCoordinates({
            placeId: selectedSuggestion.placeId,
            address,
            location: selectedSuggestion?.location,
            googleMapsLibraries,
        });
    }
    catch (error) {
        ErrorUtils.sendSentryException(error);
        suggestionLatLng = {};
    }
    const isMonthly = parkingSubType === 'monthly';
    const isPowerBooking = parkingSubType === 'power_booking';
    const googlePlaceId = selectedSuggestion.placeId || suggestionLatLng.placeId;
    const params = {
        latitude: suggestionLatLng.latitude,
        longitude: suggestionLatLng.longitude,
        search_string: address,
        google_places_id: googlePlaceId,
        google_places_place_id: googlePlaceId,
        monthly: parkingSubType === 'monthly',
    };
    const { data: { data: searchParams }, } = await SearchAPI.cleanParams(params);
    const { page_info: { page_type: newPageType, setup: { city: { id: cityId, slug: citySlug } = {}, destination: { id: destinationId, is_airport } = {
        is_airport: false,
    }, }, }, } = searchParams;
    const hasParkingChanged = parkingSubType !== originalParkingSubType;
    let hasSearchChanged = originalLatitude !== searchParams.latitude ||
        originalLongitude !== searchParams.longitude ||
        hasParkingChanged;
    let pageType = hasSearchChanged &&
        !isMonthlyHourlySwitch(originalParkingSubType, parkingSubType)
        ? newPageType
        : originalPageType;
    // Hard reset filters if switching between monthly and hourly
    if (isMonthlyHourlySwitch(originalParkingSubType, parkingSubType)) {
        dispatch(resetFilters());
    }
    // handle event search updates
    if (!hasParkingChanged && originalParkingSubType === 'event') {
        pageType = Page.EVENT;
        const eventSuggestion = selectedSuggestion;
        // recalculate hasSearchChanged for event search
        hasSearchChanged =
            eventSuggestion &&
                eventSuggestion?.id &&
                event &&
                event?.id &&
                eventSuggestion.id !== event.id;
    }
    let id = hasSearchChanged
        ? pageType === Page.CITY
            ? cityId
            : pageType === Page.DESTINATION || pageType === Page.VENUE
                ? (destinationId || destination?.id)
                : null
        : originalId;
    // Below if statement handles if going from event to a different page and the search has changed, we try to use the
    if (originalParkingSubType === 'event' &&
        selectedSuggestion?.id === event.id) {
        id = (destinationId || destination?.id);
        if (parkingSubType === 'hourly')
            pageType = Page.DESTINATION; // Only make sure its a destination if its an event passing straight over
    }
    if (parkingSubType === 'event') {
        id = event?.id;
    }
    const searchString = hasSearchChanged
        ? selectedSuggestion.description
        : originalSearchString;
    const latitude = hasSearchChanged
        ? suggestionLatLng.latitude
        : originalLatitude;
    const longitude = hasSearchChanged
        ? suggestionLatLng.longitude
        : originalLongitude;
    const defaultParams = id
        ? {
            ...getUrlParamAsObject('operator_id'), // preserve operator_id
        }
        : {
            latitude,
            longitude,
            search_string: searchString,
            ...getUrlParamAsObject('operator_id'),
        };
    let airportParams = {};
    // If the search is not updated, search params will not have airport info
    // But we know its airport from original search and hence reusing the known info
    if (is_airport ||
        (!hasSearchChanged && originalParkingSubType === 'airport')) {
        let airportCode = null;
        if (!hasSearchChanged && destination?.airport) {
            airportCode = destination.airport.iata_code;
        }
        else {
            airportCode = await getAirportCodeByDestinationId(Number(id || destination?.id));
        }
        if (airportCode) {
            airportParams = {
                kind: 'airport',
                id: airportCode,
                airport: true,
                hide_modal: true,
                latitude: null,
                longitude: null,
                search_string: null,
            };
        }
    }
    const handlePowerBookingSearch = () => {
        const queryStarts = [];
        const queryEnds = [];
        powerBookingPeriods?.forEach(({ starts: periodStarts, ends: periodEnds }) => {
            queryStarts.push(periodStarts);
            queryEnds.push(periodEnds);
        });
        return routeToSearch({
            method: (url) => {
                window.location.replace(url);
            },
            pageType,
            id,
            queryParams: {
                ...defaultParams,
                starts: queryStarts,
                ends: queryEnds,
                power_booking: true,
                ...(powerBookingSource && {
                    pb_source: powerBookingSource,
                }),
                ...getUrlParamAsObject('hide_event_modal'),
                ...getUrlParamAsObject('spot-id'),
            },
            historyState: { searchParams },
        });
    };
    const handleAnyTimezoneChange = async () => {
        const { data: { data: { timezone: newTimezone }, }, } = await CityAPI.get(citySlug);
        if (timezone && timezone !== newTimezone) {
            routeToSearch({
                method: (url) => {
                    window.location.replace(url);
                },
                pageType,
                id,
                queryParams: {
                    ...(isMonthly && { monthly: true }),
                    ...defaultParams,
                    starts: null,
                    ends: null,
                    ...airportParams,
                    view: getRouteParam(paramNameOptions.VIEW),
                    ...getUrlParamAsObject('spot-id'),
                },
                historyState: { searchParams },
            });
            return true;
        }
        return false;
    };
    // catchall search
    const handleCatchallSearch = () => {
        routeToSearch({
            method: (url) => {
                window.location.replace(url);
            },
            pageType,
            id,
            queryParams: {
                ...(isMonthly && { monthly: true }),
                ...defaultParams,
                starts,
                ends: isMonthly ? null : ends,
                ...airportParams,
                ...getUrlParamAsObject('hide_event_modal'),
                view: getRouteParam(paramNameOptions.VIEW),
            },
            historyState: { searchParams },
        });
    };
    const handleSearchLocationUnchanged = () => {
        onClose?.();
        if (parkingSubType !== 'event') {
            // we reload the page for event search
            // so we don't need to update times
            dispatch(updateTimes({
                starts,
                ends,
                isMonthly,
            }));
        }
        // update URL params
        routeToSearch({
            method: (url) => {
                history.replace(url, {
                    reload: parkingSubType === 'event',
                });
            },
            pageType,
            id,
            queryParams: {
                ...(isMonthly && { monthly: true }),
                ...defaultParams,
                starts,
                ends: isMonthly ? null : ends,
                ...airportParams,
                view: getRouteParam(paramNameOptions.VIEW),
                ...getUrlParamAsObject('hide_event_modal'),
                ...getUrlParamAsObject('spot-id'),
            },
            historyState: { searchParams },
        });
    };
    // handle power booking
    if (isPowerBooking) {
        return handlePowerBookingSearch();
    }
    // Avoid full page refresh when possible
    if (!hasSearchChanged && originalParkingSubType !== 'power_booking') {
        return handleSearchLocationUnchanged();
    }
    // handle timezone change
    if (originalCitySlug && originalCitySlug !== citySlug) {
        const hasTimeZoneChanged = await handleAnyTimezoneChange();
        if (hasTimeZoneChanged) {
            return; // timezone change already handled
        }
    }
    handleCatchallSearch();
};
